import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import "./CompanyPaymentSettings.css";

const API_URL = "/api";

const emptyNewMethod = {
  code: "",
  name: "",
  bank_account_id: "",
  requires_cash_session: false,
  allows_change: false,
  is_active: true,
  sort_order: 10,
};

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const normalizeMethod = (method) => ({
  ...method,
  name: method.name || "",
  bank_account_id: method.bank_account_id ?? "",
  requires_cash_session: method.requires_cash_session ?? false,
  allows_change: method.allows_change ?? false,
  is_active: method.is_active ?? true,
  sort_order: method.sort_order ?? 1,
});

export default function CompanyPaymentSettings({
  readOnly = false,
}) {
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [methods, setMethods] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [newMethod, setNewMethod] = useState(emptyNewMethod);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [methodsResponse, accountsResponse] = await Promise.all([
        axios.get(
          `${API_URL}/settings/payment-methods/me`,
          { headers: getHeaders() }
        ),
        axios.get(
          `${API_URL}/banks/me/accounts`,
          { headers: getHeaders() }
        ),
      ]);

      setMethods(methodsResponse.data.map(normalizeMethod));

      setBankAccounts(
        Array.isArray(accountsResponse.data)
          ? accountsResponse.data
          : accountsResponse.data.items || []
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible cargar los métodos de pago."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMethodChange = (id, event) => {
    const { name, value, type, checked } = event.target;

    setMethods((previous) =>
      previous.map((method) =>
        method.id === id
          ? {
              ...method,
              [name]: type === "checkbox" ? checked : value,
            }
          : method
      )
    );
  };

  const saveMethod = async (method) => {
    if (readOnly) return;

    try {
      setSavingId(method.id);

      const response = await axios.put(
        `${API_URL}/settings/payment-methods/me/${method.id}`,
        {
          name: method.name.trim(),
          bank_account_id:
            method.bank_account_id === ""
              ? null
              : Number(method.bank_account_id),
          requires_cash_session: method.requires_cash_session,
          allows_change: method.allows_change,
          is_active: method.is_active,
          sort_order: Number(method.sort_order || 1),
        },
        { headers: getHeaders() }
      );

      setMethods((previous) =>
        previous.map((item) =>
          item.id === method.id
            ? normalizeMethod(response.data)
            : item
        )
      );

      toast.success(
        `${response.data.name} actualizado correctamente.`
      );
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible actualizar el método de pago."
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleNewMethodChange = (event) => {
    const { name, value, type, checked } = event.target;

    setNewMethod((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const createMethod = async (event) => {
    event.preventDefault();

    if (readOnly) return;

    const code = newMethod.code
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "_");

    if (!code || !newMethod.name.trim()) {
      toast.warning("Código y nombre son obligatorios.");
      return;
    }

    try {
      setCreating(true);

      const response = await axios.post(
        `${API_URL}/settings/payment-methods/me`,
        {
          code,
          name: newMethod.name.trim(),
          bank_account_id:
            newMethod.bank_account_id === ""
              ? null
              : Number(newMethod.bank_account_id),
          requires_cash_session:
            newMethod.requires_cash_session,
          allows_change: newMethod.allows_change,
          is_active: newMethod.is_active,
          sort_order: Number(newMethod.sort_order || 1),
        },
        { headers: getHeaders() }
      );

      setMethods((previous) => [
        ...previous,
        normalizeMethod(response.data),
      ]);

      setNewMethod(emptyNewMethod);
      toast.success("Método de pago creado correctamente.");
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.detail ||
          "No fue posible crear el método de pago."
      );
    } finally {
      setCreating(false);
    }
  };

  const renderBankOptions = () =>
    bankAccounts
      .filter((account) => account.is_active)
      .map((account) => (
        <option key={account.id} value={account.id}>
          {account.bank_name} - {account.account_number}
        </option>
      ));

  if (loading) {
    return (
      <div className="settings-loading">
        Cargando métodos de pago...
      </div>
    );
  }

  return (
    <div className="payment-settings">
      <div className="payment-settings-heading">
        <div>
          <h3>Métodos de pago</h3>
          <p>
            Configura los medios disponibles en el POS y
            relaciónalos con tus cuentas bancarias.
          </p>
        </div>
      </div>

      <section className="payment-methods-section">
        <h4>Métodos disponibles</h4>

        <div className="payment-methods-list">
          {methods.map((method) => (
            <article
              className="payment-method-card"
              key={method.id}
            >
              <div className="payment-method-card-header">
                <div>
                  <span className="payment-method-code">
                    {method.code}
                  </span>
                  <h5>{method.name || "Sin nombre"}</h5>
                </div>
              </div>

              <fieldset
                className="payment-settings-fieldset"
                disabled={readOnly}
              >
                <label
                  className={
                    method.is_active
                      ? "payment-active active"
                      : "payment-active inactive"
                  }
                >
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={method.is_active}
                    onChange={(event) =>
                      handleMethodChange(method.id, event)
                    }
                  />
                  {method.is_active ? "Activo" : "Inactivo"}
                </label>

                <div className="payment-method-grid">
                  <div className="floating-field">
                    <input
                      id={`name-${method.id}`}
                      name="name"
                      value={method.name}
                      onChange={(event) =>
                        handleMethodChange(method.id, event)
                      }
                      placeholder=" "
                      maxLength={80}
                    />
                    <label htmlFor={`name-${method.id}`}>
                      Nombre visible
                    </label>
                  </div>

                  <div className="floating-field">
                    <input
                      id={`sort_order-${method.id}`}
                      name="sort_order"
                      type="number"
                      min="1"
                      value={method.sort_order}
                      onChange={(event) =>
                        handleMethodChange(method.id, event)
                      }
                      placeholder=" "
                    />
                    <label htmlFor={`sort_order-${method.id}`}>
                      Orden en POS
                    </label>
                  </div>

                  <div className="floating-field payment-field-wide">
                    <select
                      id={`bank_account_id-${method.id}`}
                      name="bank_account_id"
                      value={method.bank_account_id}
                      onChange={(event) =>
                        handleMethodChange(method.id, event)
                      }
                    >
                      <option value="">
                        Sin cuenta bancaria asociada
                      </option>
                      {renderBankOptions()}
                    </select>
                    <label htmlFor={`bank_account_id-${method.id}`}>
                      Cuenta bancaria
                    </label>
                  </div>
                </div>

                <div className="payment-method-options">
                  <label className="payment-option">
                    <input
                      type="checkbox"
                      name="requires_cash_session"
                      checked={method.requires_cash_session}
                      onChange={(event) =>
                        handleMethodChange(method.id, event)
                      }
                    />
                    Requiere caja abierta
                  </label>

                  <label className="payment-option">
                    <input
                      type="checkbox"
                      name="allows_change"
                      checked={method.allows_change}
                      onChange={(event) =>
                        handleMethodChange(method.id, event)
                      }
                    />
                    Permite entregar cambio
                  </label>
                </div>
              </fieldset>

              <div className="payment-method-actions">
                {!readOnly ? (
                  <button
                    type="button"
                    onClick={() => saveMethod(method)}
                    disabled={savingId === method.id}
                  >
                    {savingId === method.id
                      ? "Guardando..."
                      : "Guardar cambios"}
                  </button>
                ) : (
                  <span className="settings-view-only">
                    Solo consulta
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="payment-methods-section">
        <h4>Agregar método de pago</h4>

        <form
          className="payment-new-method-form"
          onSubmit={createMethod}
        >
          <fieldset
            className="payment-settings-fieldset"
            disabled={readOnly}
          >
            <div className="payment-method-grid">
              <div className="floating-field">
                <input
                  id="new-code"
                  name="code"
                  value={newMethod.code}
                  onChange={handleNewMethodChange}
                  placeholder=" "
                  maxLength={30}
                />
                <label htmlFor="new-code">Código interno</label>
              </div>

              <div className="floating-field">
                <input
                  id="new-name"
                  name="name"
                  value={newMethod.name}
                  onChange={handleNewMethodChange}
                  placeholder=" "
                  maxLength={80}
                />
                <label htmlFor="new-name">Nombre visible</label>
              </div>

              <div className="floating-field">
                <input
                  id="new-sort-order"
                  name="sort_order"
                  type="number"
                  min="1"
                  value={newMethod.sort_order}
                  onChange={handleNewMethodChange}
                  placeholder=" "
                />
                <label htmlFor="new-sort-order">
                  Orden en POS
                </label>
              </div>

              <div className="floating-field">
                <select
                  id="new-bank-account"
                  name="bank_account_id"
                  value={newMethod.bank_account_id}
                  onChange={handleNewMethodChange}
                >
                  <option value="">
                    Sin cuenta bancaria asociada
                  </option>
                  {renderBankOptions()}
                </select>
                <label htmlFor="new-bank-account">
                  Cuenta bancaria
                </label>
              </div>
            </div>

            <div className="payment-method-options">
              <label className="payment-option">
                <input
                  type="checkbox"
                  name="requires_cash_session"
                  checked={newMethod.requires_cash_session}
                  onChange={handleNewMethodChange}
                />
                Requiere caja abierta
              </label>

              <label className="payment-option">
                <input
                  type="checkbox"
                  name="allows_change"
                  checked={newMethod.allows_change}
                  onChange={handleNewMethodChange}
                />
                Permite entregar cambio
              </label>

              <label className="payment-option">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={newMethod.is_active}
                  onChange={handleNewMethodChange}
                />
                Activar al crear
              </label>
            </div>
          </fieldset>

          <div className="payment-new-method-actions">
            {!readOnly ? (
              <button type="submit" disabled={creating}>
                {creating
                  ? "Creando..."
                  : "Crear método de pago"}
              </button>
            ) : (
              <span className="settings-view-only">
                Solo consulta
              </span>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}